import React, { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';
import { toast } from 'sonner';
import {
  ReceiptText, FileSpreadsheet, ArrowLeftRight, BadgeCheck, QrCode, Truck,
  Download, X, AlertTriangle, Info, CheckCircle2, Circle, Plus,
} from 'lucide-react';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const inp = 'w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]';
const lbl = 'block text-[10px] font-display font-bold uppercase tracking-[0.14em] text-slate-500 mb-1';
const TH = 'px-3 py-2 text-left text-[10px] font-display font-bold uppercase tracking-wider text-slate-500';
const THR = 'px-3 py-2 text-right text-[10px] font-display font-bold uppercase tracking-wider text-slate-500';

const thisMonth = new Date().toISOString().slice(0, 7);

function exportCSV(filename, columns, rows) {
  const esc = (v) => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [columns.map((c) => esc(c.label)).join(',')]
    .concat(rows.map((r) => columns.map((c) => esc(typeof c.value === 'function' ? c.value(r) : r[c.key])).join(','))).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = filename; a.click(); URL.revokeObjectURL(a.href);
}

const Tile = ({ label, value, tone = 'default', testId }) => {
  const tones = { default: 'border-slate-200', amber: 'border-amber-300 bg-amber-50', rose: 'border-rose-300 bg-rose-50', green: 'border-emerald-300 bg-emerald-50' };
  return (
    <div className={`border rounded-sm px-4 py-2.5 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] font-display font-bold uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="font-display text-lg font-extrabold text-[#3b4872]">{value}</div>
    </div>
  );
};

// ─── GSTR-1 tab ──────────────────────────────────────────────────────────────
function GSTR1Tab({ month }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); apiGet('/finance/gst/gstr1-data', { month }).then(setD).catch(() => toast.error('Failed to load GSTR-1 data')); }, [month]);
  const cols = [
    { key: 'buyer_gstin', label: 'GSTIN' }, { key: 'bill_no', label: 'Invoice No' }, { key: 'bill_date', label: 'Invoice Date' },
    { key: 'place_of_supply', label: 'POS' }, { key: 'sac_code', label: 'SAC' }, { key: 'taxable_value', label: 'Taxable' },
    { key: 'rate_pct', label: 'Rate %' }, { key: 'igst', label: 'IGST' }, { key: 'cgst', label: 'CGST' }, { key: 'sgst', label: 'SGST' },
    { key: 'invoice_value', label: 'Invoice Value' },
  ];
  return (
    <div data-testid="gstr1-tab">
      {d && (
        <div className="flex flex-wrap gap-3 mb-3">
          <Tile label="Taxable Outward" value={formatINR(d.summary.taxable_total)} testId="gstr1-taxable" />
          <Tile label="Tax (CGST+SGST+IGST)" value={formatINR(d.summary.tax_total)} tone="amber" testId="gstr1-tax" />
          <button className="ml-auto flex items-center gap-1 text-xs font-bold text-[#3b4872] hover:underline" onClick={() => exportCSV(`GSTR1-B2B-${month}.csv`, cols, d.rows)} data-testid="gstr1-export"><Download size={12} /> B2B CSV</button>
        </div>
      )}
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full text-xs">
          <thead className="bg-slate-50"><tr><th className={TH}>GSTIN</th><th className={TH}>Invoice</th><th className={TH}>Date</th><th className={TH}>POS</th><th className={TH}>SAC</th><th className={THR}>Taxable</th><th className={THR}>Rate</th><th className={THR}>IGST</th><th className={THR}>CGST</th><th className={THR}>SGST</th></tr></thead>
          <tbody>
            {!d && <tr><td colSpan={10} className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {d && d.rows.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-slate-500" data-testid="gstr1-empty">No client bills dated in {month}.</td></tr>}
            {(d?.rows || []).map((r) => (
              <tr key={r.bill_id} className="border-t border-slate-100" data-testid={`gstr1-row-${r.bill_no}`}>
                <td className="px-3 py-2">{r.buyer_gstin || <span className="text-amber-600 font-bold">B2C / no GSTIN</span>}</td>
                <td className="px-3 py-2 font-bold">{r.bill_no}</td>
                <td className="px-3 py-2">{formatDate(r.bill_date)}</td>
                <td className="px-3 py-2">{r.place_of_supply}</td>
                <td className="px-3 py-2">{r.sac_code}</td>
                <td className="px-3 py-2 text-right font-display">{formatINR(r.taxable_value)}</td>
                <td className="px-3 py-2 text-right">{r.rate_pct}%</td>
                <td className="px-3 py-2 text-right">{r.igst ? formatINR(r.igst) : '—'}</td>
                <td className="px-3 py-2 text-right">{r.cgst ? formatINR(r.cgst) : '—'}</td>
                <td className="px-3 py-2 text-right">{r.sgst ? formatINR(r.sgst) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── GSTR-3B tab ─────────────────────────────────────────────────────────────
function GSTR3BTab({ month }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); apiGet('/finance/gst/gstr3b-data', { month }).then(setD).catch(() => toast.error('Failed to load 3B data')); }, [month]);
  if (!d) return <div className="text-slate-400 text-sm py-6">Loading…</div>;
  return (
    <div className="space-y-4 max-w-3xl" data-testid="gstr3b-tab">
      <div className="border border-slate-200 rounded-sm">
        <div className="px-4 py-2 bg-slate-50 text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">3.1 Outward supplies</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-slate-100">
          {[['Taxable', d.outward.taxable], ['IGST', d.outward.igst], ['CGST', d.outward.cgst], ['SGST', d.outward.sgst], ['Total Tax', d.outward.tax_total]].map(([l, v]) => (
            <div key={l} className="bg-white px-3 py-2"><div className="text-[10px] uppercase font-bold text-slate-400">{l}</div><div className="font-display font-bold" data-testid={`b3-out-${l}`}>{formatINR(v)}</div></div>
          ))}
        </div>
      </div>
      <div className="border border-slate-200 rounded-sm">
        <div className="px-4 py-2 bg-slate-50 text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">4. Input Tax Credit (eligible → 2B-matched → claimable)</div>
        <div className="divide-y divide-slate-100 text-sm">
          <div className="flex justify-between px-4 py-2"><span>ITC eligible (invoices, excl. blocked & RCM)</span><b className="font-display" data-testid="b3-itc-eligible">{formatINR(d.itc.eligible_total)}</b></div>
          <div className="flex justify-between px-4 py-2 text-amber-700"><span>Less: not yet appearing in GSTR-2B (unmatched)</span><b className="font-display" data-testid="b3-itc-unmatched">− {formatINR(d.itc.less_2b_unmatched)}</b></div>
          <div className="flex justify-between px-4 py-2 bg-emerald-50"><span className="font-bold">Claimable this month</span><b className="font-display text-emerald-700" data-testid="b3-itc-claimable">{formatINR(d.itc.claimable)}</b></div>
          <div className="flex justify-between px-4 py-2 text-rose-600"><span>Blocked u/r 17(5) — excluded</span><b className="font-display" data-testid="b3-itc-blocked">{formatINR(d.itc.blocked_17_5)}</b></div>
          <div className="flex justify-between px-4 py-2"><span>RCM ITC (self-invoice)</span><b className="font-display" data-testid="b3-itc-rcm">{formatINR(d.itc.rcm_itc)}</b></div>
        </div>
      </div>
      <div className="border border-slate-200 rounded-sm">
        <div className="flex justify-between px-4 py-2"><span className="text-sm">RCM payable in cash</span><b className="font-display" data-testid="b3-rcm-payable">{formatINR(d.rcm.payable_cash)}</b></div>
        <div className="flex justify-between px-4 py-3 bg-[#3b4872] text-white rounded-b-sm"><span className="font-display font-bold uppercase text-xs tracking-wider self-center">Net cash liability estimate</span><b className="font-display text-lg" data-testid="b3-net-cash">{formatINR(d.net_cash_liability_estimate)}</b></div>
      </div>
      <div className="text-[10px] text-slate-500 flex items-start gap-1"><Info size={11} className="mt-0.5 shrink-0" /> {d.note}</div>
    </div>
  );
}

// ─── RCM tab ─────────────────────────────────────────────────────────────────
function RCMTab({ month }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); apiGet('/finance/gst/rcm-register', { month }).then(setD).catch(() => {}); }, [month]);
  return (
    <div data-testid="rcm-tab">
      {d && <div className="mb-3"><Tile label="RCM tax payable (cash)" value={formatINR(d.summary.rcm_tax_payable)} tone="amber" testId="rcm-payable-tile" /></div>}
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr><th className={TH}>Invoice</th><th className={TH}>Date</th><th className={TH}>Vendor</th><th className={TH}>Category</th><th className={THR}>Taxable</th><th className={THR}>GST (RCM)</th><th className={TH}>Override Note</th></tr></thead>
          <tbody>
            {d && d.rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-500" data-testid="rcm-empty">No reverse-charge purchases in {month}. GTA / legal / security vendors auto-flag here.</td></tr>}
            {(d?.rows || []).map((r) => (
              <tr key={r.invoice_id} className="border-t border-slate-100" data-testid={`rcm-row-${r.invoice_no}`}>
                <td className="px-3 py-2 font-bold">{r.invoice_no}</td>
                <td className="px-3 py-2 text-xs">{formatDate(r.invoice_date)}</td>
                <td className="px-3 py-2">{r.vendor_name}</td>
                <td className="px-3 py-2 text-xs font-bold uppercase">{r.rcm_category}</td>
                <td className="px-3 py-2 text-right font-display">{formatINR(r.taxable)}</td>
                <td className="px-3 py-2 text-right font-display font-bold">{formatINR(r.gst_amount)}</td>
                <td className="px-3 py-2 text-[10px] text-slate-500">{r.rcm_override_reason || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── ITC tab ─────────────────────────────────────────────────────────────────
function ITCTab({ month }) {
  const [d, setD] = useState(null);
  const [eligF, setEligF] = useState('');
  const load = useCallback(() => {
    apiGet('/finance/gst/itc-register', { month, ...(eligF ? { eligibility: eligF } : {}) }).then(setD).catch(() => {});
  }, [month, eligF]);
  useEffect(() => { setD(null); load(); }, [load]);

  const setFlags = async (r, patch, needsReason = false) => {
    let reason = '';
    if (needsReason) {
      reason = window.prompt('Overriding the auto-suggested RCM flag requires a reason (e.g. "GTA opted forward charge"):');
      if (!reason) return;
    }
    try {
      await apiPost(`/finance/gst/itc/${r.invoice_id}/set`, { ...patch, reason });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  return (
    <div data-testid="itc-tab">
      <div className="flex items-center gap-2 mb-3">
        <select className="border border-slate-300 rounded-sm px-2 py-1.5 text-xs" value={eligF} onChange={(e) => setEligF(e.target.value)} data-testid="itc-elig-filter">
          <option value="">All eligibility</option>
          {['eligible', 'blocked_17_5', 'rcm_self_invoice'].map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        {d && <span className="text-xs text-slate-500">{d.summary.count} invoices · GST {formatINR(d.summary.gst_total)}</span>}
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr><th className={TH}>Invoice</th><th className={TH}>Vendor</th><th className={THR}>GST</th><th className={TH}>Eligibility</th><th className={TH}>In GSTR-2B?</th><th className={TH}>RCM</th></tr></thead>
          <tbody>
            {d && d.rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500" data-testid="itc-empty">No GST-bearing vendor invoices in {month}.</td></tr>}
            {(d?.rows || []).map((r) => (
              <tr key={r.invoice_id} className="border-t border-slate-100" data-testid={`itc-row-${r.invoice_no}`}>
                <td className="px-3 py-2 font-bold">{r.invoice_no}<div className="text-[10px] text-slate-400">{formatDate(r.invoice_date)}</div></td>
                <td className="px-3 py-2">{r.vendor_name}</td>
                <td className="px-3 py-2 text-right font-display font-bold">{formatINR(r.gst_amount)}</td>
                <td className="px-3 py-2">
                  <select className="border border-slate-200 rounded-sm px-1.5 py-1 text-xs" value={r.itc_eligibility} onChange={(e) => setFlags(r, { itc_eligibility: e.target.value })} data-testid={`itc-elig-${r.invoice_no}`}>
                    {['eligible', 'blocked_17_5', 'rcm_self_invoice'].map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => setFlags(r, { gstr2b_matched: !r.gstr2b_matched })} className={`flex items-center gap-1 text-xs font-bold ${r.gstr2b_matched ? 'text-emerald-600' : 'text-slate-400 hover:text-slate-600'}`} data-testid={`itc-2b-${r.invoice_no}`}>
                    {r.gstr2b_matched ? <CheckCircle2 size={13} /> : <Circle size={13} />} 2B
                  </button>
                </td>
                <td className="px-3 py-2">
                  <button onClick={() => setFlags(r, { rcm_applicable: !r.rcm_applicable }, true)} className={`text-xs font-bold ${r.rcm_applicable ? 'text-amber-700' : 'text-slate-400 hover:text-slate-600'}`} data-testid={`itc-rcm-${r.invoice_no}`}>
                    {r.rcm_applicable ? `RCM · ${r.rcm_category || ''}` : 'forward'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-slate-500 mt-2">Tick "2B" once the invoice appears in your GSTR-2B — only 2B-matched eligible invoices count as claimable in 3B.</div>
    </div>
  );
}

// ─── E-invoice tab ───────────────────────────────────────────────────────────
function EInvoiceTab() {
  const [d, setD] = useState(null);
  const [irnBill, setIrnBill] = useState(null);
  const [irnForm, setIrnForm] = useState({ irn: '', ack_no: '', ack_date: '' });
  const load = useCallback(() => apiGet('/finance/gst/einvoice-pending').then(setD).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const saveIrn = async () => {
    if (!irnForm.irn) return toast.error('IRN is required');
    try {
      await apiPost(`/finance/gst/einvoice/${irnBill.bill_id}/set-irn`, irnForm);
      toast.success('IRN recorded'); setIrnBill(null); setIrnForm({ irn: '', ack_no: '', ack_date: '' }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div data-testid="einvoice-tab">
      <div className="border border-sky-200 bg-sky-50 rounded-sm px-4 py-2.5 text-xs text-sky-800 mb-2 flex items-start gap-2" data-testid="einvoice-info-banner">
        <Info size={13} className="mt-0.5 shrink-0" /> IRN generation happens in your accountant's tool; record the IRN here for traceability.
      </div>
      {d && (
        <div className="border border-amber-300 bg-amber-50 rounded-sm px-4 py-2.5 text-xs text-amber-800 mb-3 flex items-start gap-2" data-testid="einvoice-threshold-banner">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>Current e-invoice threshold: <b>₹{d.einvoice_threshold_cr} Cr</b> aggregate turnover — verify with your CA, this changes by notification.
            FY turnover so far: <b>{formatINR(d.fy_turnover)}</b> → e-invoice {d.einvoice_mandatory ? <b className="text-rose-700">MANDATORY</b> : 'not yet mandatory'}.</span>
        </div>
      )}
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr><th className={TH}>Bill</th><th className={TH}>Date</th><th className={TH}>Buyer GSTIN</th><th className={THR}>Invoice Value</th><th className={TH}>IRN Status</th><th className={TH}></th></tr></thead>
          <tbody>
            {d && d.items.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500" data-testid="einvoice-empty">No bills pending IRN.</td></tr>}
            {(d?.items || []).map((r) => (
              <tr key={r.bill_id} className="border-t border-slate-100" data-testid={`einvoice-row-${r.bill_no}`}>
                <td className="px-3 py-2 font-bold">{r.bill_no}</td>
                <td className="px-3 py-2 text-xs">{formatDate(r.bill_date)}</td>
                <td className="px-3 py-2 text-xs">{r.buyer_gstin || '—'}</td>
                <td className="px-3 py-2 text-right font-display">{formatINR(r.invoice_value)}</td>
                <td className="px-3 py-2"><span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-amber-50 text-amber-700 border border-amber-200 rounded-sm">pending</span></td>
                <td className="px-3 py-2"><button onClick={() => setIrnBill(r)} className="text-[11px] font-bold text-[#3b4872] hover:underline" data-testid={`einvoice-set-irn-${r.bill_no}`}>Paste IRN</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {irnBill && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/30" data-testid="irn-drawer">
          <div className="bg-white h-full w-[440px] max-w-full p-5 shadow-2xl">
            <div className="flex items-center justify-between mb-4"><h2 className="font-display text-lg font-extrabold">Record IRN — {irnBill.bill_no}</h2><button onClick={() => setIrnBill(null)} className="text-slate-400"><X size={18} /></button></div>
            <div className="space-y-3">
              <div><label className={lbl}>IRN (64-char hash)</label><input className={inp} value={irnForm.irn} onChange={(e) => setIrnForm({ ...irnForm, irn: e.target.value })} data-testid="irn-input" /></div>
              <div><label className={lbl}>Ack No</label><input className={inp} value={irnForm.ack_no} onChange={(e) => setIrnForm({ ...irnForm, ack_no: e.target.value })} data-testid="irn-ack-no" /></div>
              <div><label className={lbl}>Ack Date</label><input type="date" className={inp} value={irnForm.ack_date} onChange={(e) => setIrnForm({ ...irnForm, ack_date: e.target.value })} data-testid="irn-ack-date" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="px-4 py-2 text-xs font-bold uppercase border border-slate-300 rounded-sm" onClick={() => setIrnBill(null)}>Cancel</button>
              <button className="px-4 py-2 text-xs font-bold uppercase text-white bg-[#3b4872] rounded-sm" onClick={saveIrn} data-testid="irn-save">Save IRN</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── E-way tab ───────────────────────────────────────────────────────────────
function EwayTab({ month, onMonthChange }) {
  const [d, setD] = useState(null);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ ewb_no: '', date: new Date().toISOString().slice(0, 10), doc_ref: '', vehicle_no: '', from_place: '', to_place: '', from_state: '27', to_state: '27', value: '', valid_till: '' });
  const load = useCallback(() => apiGet('/finance/gst/eway-log', { month }).then(setD).catch(() => {}), [month]);
  useEffect(() => { load(); }, [load]);
  const u = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (Number(f.value) <= 0) return toast.error('Consignment value required');
    try {
      const res = await apiPost('/finance/gst/eway-log', { ...f, value: Number(f.value) });
      toast.success(res.required ? 'Logged — EWB REQUIRED for this movement' : 'Logged — EWB not mandatory at this value');
      setShow(false);
      // keep the new row visible — jump the month picker to the saved date's month
      if (f.date.slice(0, 7) !== month) onMonthChange(f.date.slice(0, 7));
      else load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div data-testid="eway-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-500">{d ? `Thresholds — inter-state ${formatINR(d.config.interstate_threshold)} · intra-state ${formatINR(d.config.intrastate_threshold)} (configurable)` : ''}</div>
        <button onClick={() => setShow(true)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase text-white bg-[#3b4872] rounded-sm" data-testid="eway-add-btn"><Plus size={13} /> Log Movement</button>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr><th className={TH}>EWB No</th><th className={TH}>Date</th><th className={TH}>Doc Ref</th><th className={TH}>Vehicle</th><th className={TH}>Route</th><th className={THR}>Value</th><th className={TH}>EWB Required</th></tr></thead>
          <tbody>
            {d && d.items.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-500" data-testid="eway-empty">No movements logged{month ? ` in ${month}` : ''}.</td></tr>}
            {(d?.items || []).map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`eway-row-${r.ewb_no || r.id.slice(0, 6)}`}>
                <td className="px-3 py-2 font-bold">{r.ewb_no || <span className="text-slate-400">—</span>}</td>
                <td className="px-3 py-2 text-xs">{formatDate(r.date)}</td>
                <td className="px-3 py-2 text-xs">{r.doc_ref || '—'}</td>
                <td className="px-3 py-2 text-xs">{r.vehicle_no || '—'}</td>
                <td className="px-3 py-2 text-xs">{r.from_place} ({r.from_state}) → {r.to_place} ({r.to_state}) {r.inter_state && <span className="text-[9px] font-bold uppercase text-sky-700">inter</span>}</td>
                <td className="px-3 py-2 text-right font-display">{formatINR(r.value)}</td>
                <td className="px-3 py-2">
                  {r.required
                    ? <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-rose-50 text-rose-700 border border-rose-200 rounded-sm" data-testid={`eway-required-${r.ewb_no || r.id.slice(0, 6)}`}>required</span>
                    : <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-slate-100 text-slate-500 rounded-sm">not mandatory</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/30" data-testid="eway-drawer">
          <div className="bg-white h-full w-[480px] max-w-full overflow-y-auto p-5 shadow-2xl">
            <div className="flex items-center justify-between mb-4"><h2 className="font-display text-lg font-extrabold">Log Goods Movement</h2><button onClick={() => setShow(false)} className="text-slate-400"><X size={18} /></button></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>EWB No (if generated)</label><input className={inp} value={f.ewb_no} onChange={(e) => u('ewb_no', e.target.value)} data-testid="eway-form-no" /></div>
              <div><label className={lbl}>Date</label><input type="date" className={inp} value={f.date} onChange={(e) => u('date', e.target.value)} data-testid="eway-form-date" /></div>
              <div><label className={lbl}>Doc Ref (invoice / DC)</label><input className={inp} value={f.doc_ref} onChange={(e) => u('doc_ref', e.target.value)} data-testid="eway-form-doc" /></div>
              <div><label className={lbl}>Vehicle No</label><input className={inp} value={f.vehicle_no} onChange={(e) => u('vehicle_no', e.target.value)} data-testid="eway-form-vehicle" /></div>
              <div><label className={lbl}>From (place)</label><input className={inp} value={f.from_place} onChange={(e) => u('from_place', e.target.value)} data-testid="eway-form-from" /></div>
              <div><label className={lbl}>To (place)</label><input className={inp} value={f.to_place} onChange={(e) => u('to_place', e.target.value)} data-testid="eway-form-to" /></div>
              <div><label className={lbl}>From State Code</label><input className={inp} value={f.from_state} onChange={(e) => u('from_state', e.target.value)} data-testid="eway-form-fromstate" /></div>
              <div><label className={lbl}>To State Code</label><input className={inp} value={f.to_state} onChange={(e) => u('to_state', e.target.value)} data-testid="eway-form-tostate" /></div>
              <div><label className={lbl}>Consignment Value (₹)</label><input type="number" className={inp} value={f.value} onChange={(e) => u('value', e.target.value)} data-testid="eway-form-value" /></div>
              <div><label className={lbl}>Valid Till</label><input type="date" className={inp} value={f.valid_till} onChange={(e) => u('valid_till', e.target.value)} data-testid="eway-form-valid" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="px-4 py-2 text-xs font-bold uppercase border border-slate-300 rounded-sm" onClick={() => setShow(false)}>Cancel</button>
              <button className="px-4 py-2 text-xs font-bold uppercase text-white bg-[#3b4872] rounded-sm" onClick={save} data-testid="eway-form-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function GSTCenter() {
  const [tab, setTab] = useUrlTab('tab', 'gstr1');
  const [month, setMonth] = useState(thisMonth);
  const TABS = [
    { id: 'gstr1', label: 'GSTR-1 Prep', icon: FileSpreadsheet },
    { id: 'gstr3b', label: 'GSTR-3B', icon: ReceiptText },
    { id: 'rcm', label: 'RCM', icon: ArrowLeftRight },
    { id: 'itc', label: 'ITC', icon: BadgeCheck },
    { id: 'einvoice', label: 'E-invoice', icon: QrCode },
    { id: 'eway', label: 'E-way Log', icon: Truck },
  ];
  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="gst-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Finance & Accounts</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2"><ReceiptText size={26} className="text-[#3b4872]" /> GST Centre</h1>
          <p className="text-sm text-slate-600 mt-1">GSTR-1/3B prep · RCM · ITC (2B-matched) · e-invoice & e-way readiness. Data prep only — your CA files the returns.</p>
        </div>
        <input type="month" className="border border-slate-300 rounded-sm px-3 py-2 text-sm" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="gst-month-picker" />
      </div>
      <div className="flex border-b border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-display font-bold border-b-2 -mb-px whitespace-nowrap ${tab === t.id ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>
      {tab === 'gstr1' && <GSTR1Tab month={month} />}
      {tab === 'gstr3b' && <GSTR3BTab month={month} />}
      {tab === 'rcm' && <RCMTab month={month} />}
      {tab === 'itc' && <ITCTab month={month} />}
      {tab === 'einvoice' && <EInvoiceTab />}
      {tab === 'eway' && <EwayTab month={month} onMonthChange={setMonth} />}
    </div>
  );
}
